/**
 * Resolves a custom domain hostname to a store slug.
 * Called on app startup when the host is not a *.usefastsell.com subdomain.
 *
 * Only resolves stores whose custom_domain_status = 'active', ensuring
 * unverified or error domains never accidentally serve a storefront.
 */
import { supabase, db } from '@/lib/supabase';
import { getTenantRootDomain } from './getSubdomain';

/**
 * Returns the store slug (subdomain or slug field) for an active custom domain,
 * or null if the hostname isn't registered / verified.
 */
export async function resolveCustomDomainSlug(hostname: string): Promise<string | null> {
  const root = getTenantRootDomain();

  // Skip known non-store hosts immediately (no DB hit)
  if (
    !hostname ||
    hostname === 'localhost' ||
    hostname.startsWith('127.') ||
    hostname === root ||
    hostname === `www.${root}` ||
    hostname.endsWith(`.${root}`) ||
    hostname.endsWith('.netlify.app')
  ) {
    return null;
  }

  try {
    // Strip www. prefix so both "www.ggg.com" and "ggg.com" resolve to the same store
    const normalized = hostname.replace(/^www\./i, '');

    const { data, error } = await db
      .from('stores')
      .select('subdomain, slug')
      .eq('custom_domain', normalized)
      .eq('custom_domain_status', 'active')   // only fully verified domains serve storefronts
      .maybeSingle();

    if (error || !data) return null;
    return data.subdomain ?? data.slug ?? null;
  } catch {
    return null;
  }
}
